import React, { useState, useCallback } from "react"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import pptxgen from "pptxgenjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons"
import { message, Modal, Radio, Tooltip } from "antd"
import { notify } from "shared/ui/Notifications/notify"
import type { RadioChangeEvent } from "antd"
import {
  calculatePptFontSize,
  canvasToPowerPointCoordsEnhanced,
  getCanvasScalingInfo,
  createCalibrationReference,
  exportTableToPptx,
  getImageDimensions,
  type CanvasToPptOptions,
  type CompressionOptions,
} from "utils/pptxgenUtils"
import { exportToExcel, type ExportToExcelOptions } from "utils/exportToExcel"

// Types
import {
  SlideForExport,
  ReportingDashboardConfig,
  PrepareTablesResult,
  TableDataCache,
  ReportingType,
} from "types/ReportingDashboard/reporting.types"
import {
  ELEMENT_TYPE,
  TEXT_ALIGN,
  TableExportPayload,
} from "types/ReportingDashboard/elements.types"
import { RTableContent, EngineOutputItem } from "types/engineOutput.types"

/**
 * Excel export option type
 */
type ExcelExportOption = "separate" | "combined"

/**
 * Screen dimensions for scaling calculations
 */
interface ScreenDimensions {
  width: number
  isMobile: boolean
  isSmall: boolean
}

/**
 * Props interface for ReportingMenuBar component
 */
export interface ReportingMenuBarProps {
  /** Slides data specifically for PPTX export */
  slides: SlideForExport[]
  /** Current reporting type (dashboard or excel) */
  reportingType?: ReportingType
  /** Function to fetch data from Excel tabs */
  getExcelTablesData: () => Promise<
    Array<{
      name: string
      data: (string | number | null)[][]
      headers?: string[]
    }>
  >
  /** Function from container to save the entire report configuration */
  saveConfiguration: (
    config?: ReportingDashboardConfig,
    showMessage?: boolean
  ) => Promise<boolean>
  /** Indicates if there are unsaved changes */
  hasUnsavedChanges?: boolean
  /** Indicates if the saveConfiguration process is ongoing */
  isSaving?: boolean
  /** Current configuration of the report */
  currentConfig: ReportingDashboardConfig
  /** Function to prepare table data before export */
  prepareTablesForExport: (
    forceRefreshAll?: boolean
  ) => Promise<PrepareTablesResult>
  /** Cache containing pre-fetched table data */
  tableDataCache: TableDataCache
}

/**
 * Component providing menu bar actions for the reporting section.
 * Handles export functionality for both PowerPoint (dashboard) and Excel (tables) formats.
 *
 * Features:
 * - Enhanced PPTX export with accurate element positioning across screen sizes
 * - Background color support for slides
 * - Excel export with separate sheets or combined format options
 * - Save functionality with unsaved changes indication
 * - Loading states and error handling
 * - Responsive scaling for different screen sizes
 * - Chart image generation and embedding
 * - Screen-independent coordinate system for 1:1 export accuracy
 */
const ReportingMenuBar: React.FC<ReportingMenuBarProps> = ({
  slides,
  reportingType = "dashboard",
  getExcelTablesData,
  saveConfiguration,
  hasUnsavedChanges = false,
  isSaving = false,
  currentConfig,
  prepareTablesForExport,
  tableDataCache,
}) => {
  // ========================================================================================
  // STATE MANAGEMENT
  // ========================================================================================

  const [exporting, setExporting] = useState<boolean>(false)
  const [isExcelModalVisible, setIsExcelModalVisible] = useState<boolean>(false)
  const [excelExportOption, setExcelExportOption] =
    useState<ExcelExportOption>("separate")

  // ========================================================================================
  // UTILITY FUNCTIONS
  // ========================================================================================

  /**
   * Get screen dimensions and scaling information
   */
  const getScreenDimensions = useCallback((): ScreenDimensions => {
    const width = window.innerWidth
    return {
      width,
      isMobile: width < 768,
      isSmall: width < 1400,
    }
  }, [])

  /**
   * Convert hex color to RGB format for PowerPoint
   * PowerPoint backgrounds accept hex colors without the # symbol
   */
  const processBackgroundColor = useCallback((color?: string): string => {
    if (!color) return "FFFFFF" // Default white

    // Remove # if present and ensure it's a valid hex color
    const cleanColor = color.replace("#", "").toUpperCase()

    // Validate hex color format (6 characters)
    if (!/^[0-9A-F]{6}$/i.test(cleanColor)) {
      console.warn(
        `Invalid background color format: ${color}, using white as fallback`
      )
      return "FFFFFF"
    }

    return cleanColor
  }, [])

  /**
   * Get canvas element and scaling information for export
   */
  const getCanvasElementAndScaling = useCallback(() => {
    const canvasElement = document.querySelector(
      '[aria-label="Reporting Canvas"]'
    ) as HTMLElement
    if (!canvasElement) {
      console.warn("Canvas element not found for export")
      return {
        canvasRef: { current: null },
        scalingInfo: {
          scaleX: 1,
          scaleY: 1,
          actualDPI: 96,
          viewportScale: 1,
          canvasScale: 1,
        },
      }
    }

    const canvasRef = { current: canvasElement }
    const scalingInfo = getCanvasScalingInfo(canvasRef)

    return { canvasRef, scalingInfo }
  }, [])

  // ========================================================================================
  // MODAL HANDLERS
  // ========================================================================================

  const showExcelExportModal = useCallback((): void => {
    setIsExcelModalVisible(true)
  }, [])

  const hideExcelExportModal = useCallback((): void => {
    setIsExcelModalVisible(false)
  }, [])

  // ========================================================================================
  // SAVE FUNCTIONALITY
  // ========================================================================================

  /**
   * Handle save report configuration
   */
  const handleSaveReport = useCallback(async (): Promise<void> => {
    if (!saveConfiguration) {
      notify.toast.error("Save functionality not available")
      return
    }
    // The saveConfiguration function (from container) is expected to handle messages
    await saveConfiguration(currentConfig)
  }, [saveConfiguration, currentConfig])

  // ========================================================================================
  // EXCEL EXPORT FUNCTIONALITY
  // ========================================================================================

  /**
   * Handle Excel export with different format options
   */
  const handleExcelExport = useCallback(async (): Promise<void> => {
    setIsExcelModalVisible(false)

    if (typeof getExcelTablesData !== "function") {
      notify.toast.error("Excel export functionality is not available")
      return
    }

    setExporting(true)
    // keyed message flow — notify.toast has no replace-in-place; keep antd message here
    message.loading({
      content: "Generating Excel report...",
      key: "exporting",
      duration: 0,
    })

    try {
      const tablesData = await getExcelTablesData()

      if (
        !tablesData ||
        !Array.isArray(tablesData) ||
        tablesData.length === 0
      ) {
        message.warning({
          content: "No data available to export",
          key: "exporting",
          duration: 2,
        })
        return
      }

      const timestamp = new Date().toISOString().split("T")[0]

      if (excelExportOption === "separate") {
        // Export each table to separate sheets
        if (
          tablesData.every((table) => table.data && Array.isArray(table.data))
        ) {
          const tableDataArray = tablesData.map((table) => table.data)
          const sheetNames = tablesData.map((table) => table.name)

          await exportToExcel(tableDataArray, `Excel_Report_${timestamp}`, {
            sheetNames,
          } as ExportToExcelOptions)
        } else {
          notify.toast.error("Table data is not in the expected format for export")
          return
        }
      } else {
        // Export all tables to one sheet with titles
        const mergedData: (string | number | null)[][] = []

        tablesData.forEach((table, index) => {
          if (index > 0) {
            mergedData.push([]) // Empty row separator
            mergedData.push([]) // Another empty row
          }

          mergedData.push([table.name]) // Table title

          if (Array.isArray(table.data)) {
            table.data.forEach((row) => {
              if (Array.isArray(row)) {
                mergedData.push(row)
              } else {
                mergedData.push(["Invalid data format"])
              }
            })
          } else {
            mergedData.push(["No data available"])
          }
        })

        await exportToExcel(mergedData, `Excel_Report_Combined_${timestamp}`, {
          sheetName: "All Tables",
        } as ExportToExcelOptions)
      }

      message.success({
        content: "Excel report exported successfully!",
        key: "exporting",
        duration: 2,
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error"
      message.error({
        content: `Export failed: ${errorMessage}`,
        key: "exporting",
        duration: 4,
      })
    } finally {
      setExporting(false)
      message.destroy("exporting")
    }
  }, [getExcelTablesData, excelExportOption])

  // ========================================================================================
  // ENHANCED POWERPOINT EXPORT FUNCTIONALITY
  // ========================================================================================

  /**
   * Enhanced PowerPoint export with accurate cross-screen positioning
   */
  const handlePptExport = useCallback(async (): Promise<void> => {
    // Guard: Use currentConfig.dashboard.slides for checking if there's anything to export structurally
    if (
      !currentConfig ||
      !currentConfig.dashboard ||
      !Array.isArray(currentConfig.dashboard.slides) ||
      currentConfig.dashboard.slides.length === 0
    ) {
      notify.toast.warning("No dashboard slides configured to export.")
      return
    }

    // Also check the `slides` prop which contains images etc. (this is currentSlidesForExport)
    if (!slides || !Array.isArray(slides) || slides.length === 0) {
      notify.toast.warning(
        "No slide content (e.g., images, text) available to export."
      )
      return
    }

    setExporting(true)
    // keyed message flow — notify.toast has no replace-in-place; keep antd message here
    message.loading({
      content: "Initializing export...",
      key: "exporting",
      duration: 0,
    })

    // Get canvas scaling information for accurate positioning
    const { scalingInfo } = getCanvasElementAndScaling()
    const calibrationReference = createCalibrationReference()
    const { isSmall: isSmallScreen } = getScreenDimensions()

    // IMPORTANT: Get the actual canvas scale factor for responsive scaling
    const actualCanvasScale =
      "canvasScale" in scalingInfo ? scalingInfo.canvasScale : 1.0

    let effectiveTableDataCache: {
      [elementId: string]: RTableContent | EngineOutputItem
    } = { ...tableDataCache } // Start with the cache passed as a prop
    let generatedChartImages: Record<string, string> = {} // Declare at function scope

    try {
      if (
        prepareTablesForExport &&
        typeof prepareTablesForExport === "function"
      ) {
        message.loading({
          content: "Preparing all data elements...",
          key: "exporting",
          duration: 0,
        })

        // FORCE REFRESH ALL ELEMENTS - this ensures all elements are re-run with current filter
        const prepareResult = await prepareTablesForExport(true) // true = forceRefreshAll

        // Properly destructure the return value
        const { newlyFetchedData, generatedChartImages: freshChartImages } =
          prepareResult || {}

        // Update table data cache with fresh table data
        if (
          newlyFetchedData &&
          typeof newlyFetchedData === "object" &&
          Object.keys(newlyFetchedData).length > 0
        ) {
          effectiveTableDataCache = {
            ...effectiveTableDataCache,
            ...newlyFetchedData,
          }
        }

        // Update the function-scoped generatedChartImages variable
        if (freshChartImages && typeof freshChartImages === "object") {
          generatedChartImages = freshChartImages
        }

        // Determine message based on what was updated
        const tablesUpdated = newlyFetchedData
          ? Object.keys(newlyFetchedData).length
          : 0
        const chartsUpdated = generatedChartImages
          ? Object.keys(generatedChartImages).length
          : 0

        if (tablesUpdated > 0 || chartsUpdated > 0) {
          message.success({
            content: `${tablesUpdated} table(s) and ${chartsUpdated} chart(s) refreshed. Generating PowerPoint...`,
            key: "exporting",
            duration: 2,
          })
        } else {
          message.info({
            content:
              "All element data was already current. Generating PowerPoint...",
            key: "exporting",
            duration: 2,
          })
        }

        message.loading({
          content: "Generating PowerPoint...",
          key: "exporting",
          duration: 0,
        })
      }

      const pptx = new pptxgen()
      pptx.defineLayout({ name: "LAYOUT_16x9", width: 10, height: 5.625 })
      pptx.layout = "LAYOUT_16x9"

      for (let i = 0; i < slides.length; i++) {
        const slideToRender = slides[i]
        const pptSlide = pptx.addSlide()

        // Apply background color to the slide
        const backgroundColor = processBackgroundColor(
          slideToRender.backgroundColor
        )
        pptSlide.background = { color: backgroundColor }

        if (
          !slideToRender ||
          !Array.isArray(slideToRender.elements) ||
          typeof slideToRender.tableConfigs !== "object" ||
          slideToRender.tableConfigs === null
        ) {
          pptSlide.addText(`[Slide ${i + 1} Content Error]`, {
            x: 0.5,
            y: 0.5,
            w: 9,
            h: 0.5,
            fontSize: 12,
            color: "CC0000",
          })
          continue
        }

        // Add slide number
        pptSlide.addText(`Slide ${i + 1}`, {
          x: 9.5,
          y: 5.375,
          w: 0.5,
          h: 0.25,
          fontSize: 8,
          color: "AAAAAA",
          align: "right",
        })

        // Sort elements by Y position for proper layering
        const sortedElements = [...slideToRender.elements].sort(
          (a, b) => (a.y || 0) - (b.y || 0)
        )

        for (const element of sortedElements) {
          if (!element?.id || element.x == null || element.y == null) continue

          // Set default dimensions based on element type
          let defaultW = 400,
            defaultH = 300
          switch (element.type) {
            case ELEMENT_TYPE.TEXT:
              defaultW = 400
              defaultH = 40
              break
            case ELEMENT_TYPE.HEADING:
              defaultW = 400
              defaultH = 60
              break
            case ELEMENT_TYPE.IMAGE:
              defaultW = 300
              defaultH = 200
              break
            case ELEMENT_TYPE.CHART:
              defaultW = 600
              defaultH = 400
              break
            case ELEMENT_TYPE.PIE_CHART:
              defaultW = 500
              defaultH = 500
              break
            case ELEMENT_TYPE.TIME_SERIES_CHART:
              defaultW = 700
              defaultH = 450
              break
            case ELEMENT_TYPE.TABLE:
              defaultW = 800
              defaultH = 600
              break
          }

          // ENHANCED: Use the new coordinate conversion system with canvas scale awareness
          const coords = canvasToPowerPointCoordsEnhanced(
            element.x,
            element.y,
            element.width || defaultW,
            element.height || defaultH,
            {
              screenScaling: Math.min(scalingInfo.scaleX, scalingInfo.scaleY),
              verticalOffset: 0,
              compressionFactor: 1.0,
              elementType: element.type, // Pass element type for specific adjustments
              canvasScale: actualCanvasScale, // CRITICAL: Pass the actual canvas scale
              referenceElement: calibrationReference.center, // Use center reference for best accuracy
            } as CanvasToPptOptions
          )

          const elementStylingConfig = slideToRender.tableConfigs?.[element.id]

          // Handle different element types with enhanced positioning
          if (
            element.type === ELEMENT_TYPE.TEXT ||
            element.type === ELEMENT_TYPE.HEADING
          ) {
            const fontSize = calculatePptFontSize(
              element.type,
              element.fontSize
            )
            const elementColor = element.color
              ? element.color.replace("#", "")
              : "000000"

            const textOptions = {
              x: coords.x - 0.02, // Slight adjustment for text alignment
              y: coords.y,
              w: coords.w * 1.1, // Slightly wider for text overflow
              h: coords.h,
              fontSize: fontSize,
              color: elementColor,
              bold: element.isBold || element.type === ELEMENT_TYPE.HEADING,
              italic: element.isItalic || false,
              underline: element.isUnderline
                ? { style: "sng" as const }
                : undefined,
              align: (element.textAlign as TEXT_ALIGN) || TEXT_ALIGN.LEFT,
              valign: "top" as const,
              margin: 0,
              autoFit: false,
              isTextBox: true,
            }

            pptSlide.addText(element.text || "", textOptions)
          } else if (element.type === ELEMENT_TYPE.IMAGE) {
            if (element.imageSrc && element.imageSrc.startsWith("data:image")) {
              try {
                const { width: naturalWidth, height: naturalHeight } =
                  await getImageDimensions(element.imageSrc)
                const aspectRatio =
                  naturalHeight > 0 && naturalWidth > 0
                    ? naturalHeight / naturalWidth
                    : 1
                const calculatedH = coords.w * aspectRatio

                pptSlide.addImage({
                  data: element.imageSrc,
                  x: coords.x,
                  y: coords.y,
                  w: coords.w,
                  h: calculatedH,
                })
              } catch (imgError) {
                console.error(
                  `Error loading image ${element.id} for PPTX:`,
                  imgError
                )
                pptSlide.addText("[Image Error]", {
                  x: coords.x,
                  y: coords.y,
                  w: coords.w,
                  h: coords.h,
                  fontSize: 10,
                  color: "CC0000",
                  align: "center",
                  valign: "middle",
                })
              }
            } else {
              pptSlide.addText("[Image Placeholder]", {
                x: coords.x,
                y: coords.y,
                w: coords.w,
                h: coords.h,
                fontSize: 10,
                color: "888888",
                align: "center",
                valign: "middle",
              })
            }
          } else if (
            element.type === ELEMENT_TYPE.CHART ||
            element.type === ELEMENT_TYPE.PIE_CHART ||
            element.type === ELEMENT_TYPE.TIME_SERIES_CHART
          ) {
            if (generatedChartImages && generatedChartImages[element.id]) {
              // Use freshly generated chart image (highest priority)
              pptSlide.addImage({
                data: generatedChartImages[element.id],
                x: coords.x,
                y: coords.y,
                w: coords.w,
                h: coords.h,
              })
            } else if (
              elementStylingConfig &&
              "chartDataUri" in elementStylingConfig &&
              elementStylingConfig.chartDataUri
            ) {
              // Use existing chart image from config (fallback)
              pptSlide.addImage({
                data: elementStylingConfig.chartDataUri,
                x: coords.x,
                y: coords.y,
                w: coords.w,
                h: coords.h,
              })
            } else {
              pptSlide.addText(
                `[${element.type} Placeholder - Image not generated]`,
                {
                  x: coords.x,
                  y: coords.y,
                  w: coords.w,
                  h: coords.h,
                  fontSize: 10,
                  color: "888888",
                  align: "center",
                  valign: "middle",
                }
              )
            }
          } else if (element.type === ELEMENT_TYPE.TABLE) {
            // Enhanced table positioning with calibrated coordinates
            let tableY = coords.y
            const prevIdx =
              sortedElements.findIndex((el) => el.id === element.id) - 1

            if (prevIdx >= 0) {
              const prevEl = sortedElements[prevIdx]
              if (
                prevEl.type === ELEMENT_TYPE.HEADING &&
                prevEl.x != null &&
                prevEl.y != null
              ) {
                const prevCoords = canvasToPowerPointCoordsEnhanced(
                  prevEl.x,
                  prevEl.y,
                  prevEl.width || 400,
                  prevEl.height || 60,
                  {
                    screenScaling: Math.min(
                      scalingInfo.scaleX,
                      scalingInfo.scaleY
                    ),
                    elementType: prevEl.type, // Pass element type for specific adjustments
                    canvasScale: actualCanvasScale, // Pass canvas scale here too
                    referenceElement: calibrationReference.center,
                  } as CanvasToPptOptions
                )
                tableY = prevCoords.y + prevCoords.h + 0.05
              }
            } else if (tableY < 0.2) {
              tableY = 0.2
            }

            const finalTableConfigForPptx = elementStylingConfig
              ? { ...elementStylingConfig }
              : {}

            // Only process table data if this is actually a table element
            if (element.type === ELEMENT_TYPE.TABLE) {
              if (
                effectiveTableDataCache &&
                effectiveTableDataCache[element.id]
              ) {
                const tableDataFromCache = effectiveTableDataCache[element.id]
                let contentForExport = null

                // Handle both EngineOutputItem (from newlyFetchedData) and RTableContent (from original cache)
                // Type guard: Check if it's an EngineOutputItem (has content property)
                if (
                  "content" in tableDataFromCache &&
                  tableDataFromCache.content &&
                  typeof tableDataFromCache.content === "object" &&
                  "data" in tableDataFromCache.content &&
                  Array.isArray(
                    (tableDataFromCache.content as RTableContent).data
                  )
                ) {
                  // This is an EngineOutputItem with content property
                  contentForExport = tableDataFromCache.content as RTableContent
                } else if (
                  "data" in tableDataFromCache &&
                  Array.isArray((tableDataFromCache as RTableContent).data)
                ) {
                  // This is a direct RTableContent object
                  contentForExport = tableDataFromCache as RTableContent
                } else if (Array.isArray(tableDataFromCache)) {
                  // This is a raw data array
                  contentForExport = { data: tableDataFromCache, headers: [] }
                }

                if (contentForExport) {
                  // Type assertion to ensure we can add tableData property
                  ;(
                    finalTableConfigForPptx as Record<string, unknown>
                  ).tableData = {
                    content: contentForExport,
                  }
                } else {
                  console.warn(
                    `Data for table ${element.id} in effectiveTableDataCache is malformed. Placeholder will be used. Received:`,
                    tableDataFromCache
                  )
                }
              } else {
                console.warn(
                  `No data found in effectiveTableDataCache for table ${element.id}. Placeholder will be used.`
                )
              }
            }

            // Enhanced table export with improved positioning accuracy
            exportTableToPptx(
              pptSlide,
              coords.x,
              tableY,
              coords.w,
              coords.h,
              element.type === ELEMENT_TYPE.TABLE
                ? (finalTableConfigForPptx as TableExportPayload)
                : null,
              {
                compressionFactor: 0.95, // Slightly less compression for better accuracy
                fontSize: isSmallScreen ? 6 : 6, // Consistent font size
                cellPadding: 2.5, // Consistent padding
              } as CompressionOptions
            )
          }
        }
      }

      const timestamp = new Date().toISOString().split("T")[0]
      await pptx.writeFile({ fileName: `Report_${timestamp}.pptx` })

      message.success({
        content: "PowerPoint file exported successfully!",
        key: "exporting",
        duration: 2,
      })
    } catch (error) {
      console.error("Error during PowerPoint export:", error)
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error"
      message.error({
        content: `Export failed: ${errorMessage}`,
        key: "exporting",
        duration: 4,
      })
    } finally {
      setExporting(false)
      message.destroy("exporting")
    }
  }, [
    slides,
    currentConfig,
    prepareTablesForExport,
    tableDataCache,
    processBackgroundColor,
    getCanvasElementAndScaling,
    getScreenDimensions,
  ])

  // ========================================================================================
  // EVENT HANDLERS
  // ========================================================================================

  /**
   * Handle Excel export option change
   */
  const handleExportOptionChange = useCallback((e: RadioChangeEvent): void => {
    setExcelExportOption(e.target.value as ExcelExportOption)
  }, [])

  // ========================================================================================
  // RENDER
  // ========================================================================================

  return (
    <div className="inline-block relative">
      <div className="bg-white flex items-center gap-2 px-3 py-2 rounded-b-lg transition-colors w-auto">
        {reportingType === "dashboard" ? (
          <DataQButton
            onClick={handlePptExport}
            isLoading={exporting}
            isDisabled={!slides || slides.length === 0 || exporting}
            type="primary"
            size="small"
          >
            Export PPT
          </DataQButton>
        ) : (
          <DataQButton
            onClick={showExcelExportModal}
            isLoading={exporting}
            isDisabled={exporting}
            type="primary"
            size="small"
          >
            Export Excel Report
          </DataQButton>
        )}

        <DataQButton
          onClick={handleSaveReport}
          isLoading={isSaving}
          isDisabled={!saveConfiguration || isSaving}
          type={hasUnsavedChanges ? "primary" : "secondary"}
          size="small"
        >
          Save
        </DataQButton>

        <div className="relative">
          {hasUnsavedChanges && (
            <Tooltip
              title="Unsaved changes detected. Click Save to persist changes."
              placement="bottom"
            >
              <FontAwesomeIcon
                icon={faCircleExclamation}
                style={{ color: "#e09b20", height: "1.5em" }}
                className="animate-pulse"
              />
            </Tooltip>
          )}
        </div>
      </div>

      <Modal
        title="Export Excel Report"
        open={isExcelModalVisible}
        onCancel={hideExcelExportModal}
        footer={null}
      >
        <div className="mb-6">
          <p className="mb-3">Choose export format:</p>
          <Radio.Group
            value={excelExportOption}
            onChange={handleExportOptionChange}
            style={{ color: "#36C3ED" }}
          >
            <Radio
              value="separate"
              className="dataq-radio"
              style={{ marginBottom: "8px" }}
            >
              Each table on a separate sheet
            </Radio>
            <Radio value="combined" className="dataq-radio">
              All tables on one sheet with titles
            </Radio>
          </Radio.Group>
        </div>

        <div className="flex justify-end gap-3">
          <DataQButton
            onClick={hideExcelExportModal}
            size="small"
            type="secondary"
          >
            Cancel
          </DataQButton>
          <DataQButton
            onClick={handleExcelExport}
            isLoading={exporting}
            isDisabled={exporting}
            size="small"
            type="primary"
          >
            Export
          </DataQButton>
        </div>
      </Modal>
    </div>
  )
}

export default ReportingMenuBar
